import { Input } from '@heroui/react';
import React, { forwardRef } from 'react';

import { useAddressInput } from './hooks/useAddressInput';
import { PredictionsList } from './PredictionsList';
import type { AddressChangeHandler, AddressData } from './types';

interface AddressInputProps {
  id?: string;
  name?: string;
  placeholder?: string;
  label?: string;
  isRequired?: boolean;
  onAddressChange: AddressChangeHandler<AddressData>;
  className?: string;
  isCustom?: boolean;
  value?: string;
  onManualInput?: (value: string) => void;
}

export const AddressInput = forwardRef<HTMLInputElement, AddressInputProps>(
  (
    {
      onAddressChange,
      className,
      isCustom = false,
      value,
      onManualInput,
      label,
      isRequired = false,
      id,
      ...inputProps
    },
    ref
  ) => {
    const addressInput = useAddressInput({
      onAddressChange,
      isCustom,
      onManualInput,
    });

    return (
      <div className={`space-y-2 ${className || ''}`}>
        {label && (
          <label htmlFor={id} className="text-sm font-medium text-foreground">
            {label} {isRequired && <span className="text-danger">*</span>}
          </label>
        )}
        <div className="relative" ref={addressInput.inputContainerRef}>
          <Input
            {...inputProps}
            id={id}
            ref={ref}
            value={value}
            autoComplete="off"
            onChange={addressInput.handleInputChange}
            onFocus={addressInput.showPredictions}
            variant="bordered"
            size="md"
            classNames={{
              inputWrapper:
                'bg-default-50 border-default-200 hover:border-default-300 focus-within:border-primary shadow-sm hover:shadow-md transition-all duration-200',
              input: 'text-foreground bg-transparent px-3 py-2',
            }}
          />
          <div
            ref={addressInput.predictionsListRef}
            data-testid="predictions-container"
          >
            <PredictionsList
              predictions={addressInput.predictions}
              loading={addressInput.loadingPredictions}
              onPredictionClick={addressInput.onGooglePlaceSelect}
              showPredictions={addressInput.predictionsAreVisible}
              onCreateCustomBuyerClick={addressInput.onCreateCustomBuyerClick}
            />
          </div>
        </div>
      </div>
    );
  }
);
